import { getAuthToken, getCsrfTokenSync } from './auth';
import {
  isCsrfTokenExpired,
  ensureCsrfToken,
  handleCsrfTokenInconsistency,
  getCsrfTokenKeyByEnv,
} from './csrf';
import { BUSINESS_CONSTANTS } from '../config/businessConstants';

// Custom fetch function to handle CORS with credentials
export const customFetch = async (uri, options = {}) => {
  const token = getAuthToken();
  let csrfToken = getCsrfTokenSync();
  
  // If token is expired, try to get a fresh one
  if (!csrfToken || isCsrfTokenExpired()) {
    console.log('CSRF token expired or missing in customFetch, refreshing...');
    csrfToken = await ensureCsrfToken();
  }
  
  const defaultOptions = {
    method: 'POST',
    headers: {
      'Authorization': token ? `Bearer ${token}` : '',
      'Accept': 'application/json',
    },
    mode: 'cors',
    credentials: 'include', // Include cookies for cross-origin requests
  };

  // Add CSRF token if available
  if (csrfToken) {
    defaultOptions.headers[getCsrfTokenKeyByEnv()] = csrfToken;
  }

  const finalOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(uri, finalOptions);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response;
  } catch (error) {
    console.error('Fetch error:', error);
    throw error;
  }
};

// Enhanced fetch function with CSRF token refresh capability
export const fetchWithCsrf = async (uri, options = {}) => {
  const token = getAuthToken();
  
  // Ensure we have a valid CSRF token before making the request
  let csrfToken = await ensureCsrfToken();
  
  const defaultOptions = {
    method: 'POST',
    headers: {
      'Authorization': token ? `Bearer ${token}` : '',
      'Accept': 'application/json',
    },
    mode: 'cors',
    credentials: 'include',
  };

  // Add CSRF token if available
  if (csrfToken) {
    defaultOptions.headers[getCsrfTokenKeyByEnv()] = csrfToken;
  }

  const finalOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(uri, finalOptions);
    
    // If we get a CSRF token invalid status, retry with a fresh token
    if (response.status === BUSINESS_CONSTANTS.HTTP_STATUS_CODES.INVALID_CSRF) {
      console.log('[API] CSRF token invalid (499), retrying request with fresh token');
      
      try {
        // Re-read CSRF token from cookies on the retry
        const freshCsrfToken = await ensureCsrfToken();
        finalOptions.headers[getCsrfTokenKeyByEnv()] = freshCsrfToken;
        const retryResponse = await fetch(uri, finalOptions);
        
        if (retryResponse.ok) {
          console.log('Request succeeded after CSRF token refresh (499)');
          return retryResponse;
        } else if (retryResponse.status === 499) {
          // If we get 499 again, treat it as a normal error
          throw new Error(`HTTP error! status: ${retryResponse.status}`);
        }
        return retryResponse;
      } catch (retryError) {
        console.error('Failed to retry request after CSRF 499 error:', retryError);
        throw retryError;
      }
    }
    
    // If we get a 403 or 401, it might be due to expired CSRF token or inconsistency
    if (response.status === 403 || response.status === 401) {
      console.log('Received 403/401, attempting to handle CSRF token inconsistency...');
      
      // Try to handle token inconsistency
      try {
        const newCsrfToken = await handleCsrfTokenInconsistency();
        
        if (newCsrfToken && newCsrfToken !== csrfToken) {
          // Retry the request with the new token
          finalOptions.headers[getCsrfTokenKeyByEnv()] = newCsrfToken;
          const retryResponse = await fetch(uri, finalOptions);
          
          if (retryResponse.ok) {
            console.log('Request succeeded after CSRF token inconsistency handling');
            return retryResponse;
          }
        }
      } catch (inconsistencyError) {
        console.error('Failed to handle CSRF token inconsistency:', inconsistencyError);
      }
    }
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response;
  } catch (error) {
    console.error('Fetch error:', error);
    throw error;
  }
};

// Centralized fetch wrapper for all POST API calls — handles 499 CSRF retry automatically.
// Use this instead of raw fetch() in every component.
export const apiFetch = async (uri, options = {}) => {
  const response = await fetch(uri, options);

  if (response.status === BUSINESS_CONSTANTS.HTTP_STATUS_CODES.INVALID_CSRF) {
    console.log('[apiFetch] CSRF token invalid (499), retrying with fresh token from cookies');
    const freshCsrfToken = getCsrfTokenSync();
    return await fetch(uri, {
      ...options,
      headers: {
        ...options.headers,
        ...(freshCsrfToken ? { [getCsrfTokenKeyByEnv()]: freshCsrfToken } : {}),
      },
    });
  }

  return response;
};

// GraphQL request wrapper
export const graphqlRequest = async (query, variables = {}) => {
  const { GRAPHQL_URL } = await import('../config/api');
  
  const response = await fetchWithCsrf(GRAPHQL_URL, {
    method: 'POST',
    body: JSON.stringify({
      query,
      variables,
    }),
  });
  
  return response.json();
}; 